import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KeyRound, Mail, Loader2, Copy, Check } from "lucide-react";
import { toast } from "sonner";

// Admin actions to reset a user's password. Pass userId directly, or email to resolve it.
export function UserPasswordActions({ userId, email, onDone }) {
  const [resolving, setResolving] = useState(false);
  const [sending, setSending] = useState(false);
  const [genning, setGenning] = useState(false);
  const [temp, setTemp] = useState(null);
  const [copied, setCopied] = useState(false);

  const key = userId || email;

  const resolveId = async () => {
    if (userId) return userId;
    setResolving(true);
    try {
      const { data } = await api.get("/admin/users", { params: { q: email } });
      const match = (data.users || []).find((u) => (u.email || "").toLowerCase() === (email || "").toLowerCase());
      if (!match) { toast.error("No login account found for this email"); return null; }
      return match.id;
    } finally { setResolving(false); }
  };

  const sendReset = async () => {
    setSending(true);
    try {
      const id = await resolveId();
      if (!id) return;
      const { data } = await api.post(`/admin/users/${id}/send-reset`, { origin_url: window.location.origin });
      toast.success(`Reset link sent to ${data.email}`);
      onDone && onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const genTemp = async () => {
    setGenning(true);
    try {
      const id = await resolveId();
      if (!id) return;
      const { data } = await api.post(`/admin/users/${id}/temp-password`);
      setTemp(data);
      setCopied(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setGenning(false); }
  };

  const copy = () => { navigator.clipboard.writeText(temp.temporary_password); setCopied(true); toast.success("Copied"); };
  const closeTemp = () => { setTemp(null); onDone && onDone(); };
  const busy = resolving || sending || genning;

  return (
    <>
      <div className="flex items-center gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={sendReset} disabled={busy} data-testid={`send-reset-${key}`} className="rounded-none border border-black/10 text-xs">
          {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Mail className="h-3.5 w-3.5 mr-1" />} Send reset link
        </Button>
        <Button type="button" size="sm" onClick={genTemp} disabled={busy} data-testid={`temp-pw-${key}`} className="rounded-none bg-primary hover:bg-[#09090B] text-xs">
          {genning ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <KeyRound className="h-3.5 w-3.5 mr-1" />} Temp password
        </Button>
      </div>
      <Dialog open={!!temp} onOpenChange={(o) => !o && closeTemp()}>
        <DialogContent className="bg-white border-black/10 rounded-none" data-testid="temp-pw-dialog">
          <DialogHeader><DialogTitle className="font-heading">Temporary password created</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Share this with <span className="font-medium text-[#09090B]">{temp?.email}</span>. They'll be required to set a new password at their next sign-in. This is shown only once.</p>
          <div className="flex items-center gap-2 bg-[#F4F4F5] border border-black/10 p-3">
            <code className="flex-1 font-mono text-base tracking-wide" data-testid="temp-pw-value">{temp?.temporary_password}</code>
            <Button type="button" size="sm" variant="ghost" onClick={copy} data-testid="temp-pw-copy" className="rounded-none border border-black/10">
              {copied ? <Check className="h-4 w-4 text-[#10B981]" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
